/**
 * useIdentity Hook - React hook for multi-identity lifecycle management
 * Handles creation, switching, deletion, import/export of poetic identities
 */

import React, { useCallback, useEffect, useState } from 'react';
import type { UserId } from '@maco144/shared';
import type { PoeticIdentity } from '@maco144/shared';
import type { E2EIdentity } from '@maco144/shared';
import { derivePoeticUserIdBrowser } from './poetic-identity-browser';
import { IdentityStore, type StoredIdentity } from './identity-store';
import { E2EKeyStore } from './e2e-key-store';
import { E2ESessionStore } from './e2e-session-store';

export interface IdentityManager {
  // State
  currentIdentity: StoredIdentity | null;
  currentPassword: string | null; // PASSWORD for backend auth (not persisted)
  identities: StoredIdentity[];
  isAuthenticated: boolean;
  isLoading: boolean;
  currentE2EIdentity: E2EIdentity | null;

  // Operations
  // NAME: shareable identity name
  // PASSWORD: backend authentication credential (used for server communication)
  createIdentity(
    phrase: PoeticIdentity,
    name: string, // Shareable identity name
    password: string // Backend authentication password
  ): Promise<void>;
  switchIdentity(userId: UserId, password: string): Promise<void>;
  deleteIdentity(userId: UserId): Promise<void>;
  updateIdentity(userId: UserId, updates: Partial<StoredIdentity>): Promise<void>;
  listIdentities(): Promise<StoredIdentity[]>;
  autoLogin(password: string): Promise<boolean>;
  logout(): Promise<void>;
  exportIdentity(userId: UserId): Promise<string>; // Export doesn't need password (identity not encrypted)
  importIdentity(data: string, name: string, password: string): Promise<void>; // Needs new NAME and PASSWORD

  // Error handling
  error: Error | null;
  clearError(): void;
}

const IdentityContext = React.createContext<IdentityManager | null>(null);

/**
 * Provider component - wrap your app with this
 */
export function IdentityProvider({ children }: { children: React.ReactNode }) {
  const manager = useIdentityManager();
  return <IdentityContext.Provider value={manager}>{children}</IdentityContext.Provider>;
}

/**
 * Use the identity manager in any component
 */
export function useIdentity(): IdentityManager {
  const context = React.useContext(IdentityContext);
  if (!context) {
    throw new Error('useIdentity must be used within IdentityProvider');
  }
  return context;
}

// ============ Internal Hook ============

function useIdentityManager(): IdentityManager {
  const [currentIdentity, setCurrentIdentity] = useState<StoredIdentity | null>(null);
  const [currentPassword, setCurrentPassword] = useState<string | null>(null); // PASSWORD for backend (not persisted)
  const [identities, setIdentities] = useState<StoredIdentity[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [currentE2EIdentity, setCurrentE2EIdentity] = useState<E2EIdentity | null>(null);

  // Lazy-init stores
  const [identityStore] = useState(() => new IdentityStore());
  const [e2eKeyStore] = useState(() => new E2EKeyStore());
  const [e2eSessionStore, setE2ESessionStore] = useState<E2ESessionStore | null>(null);

  // Initialize stores on mount
  useEffect(() => {
    const init = async () => {
      try {
        await identityStore.init();
        await e2eKeyStore.init();

        // Try to restore active identity
        const activeId = await identityStore.getActiveUserId();
        if (activeId) {
          const identity = await identityStore.getIdentity(activeId);
          if (identity) {
            setCurrentIdentity(identity);
            // Don't auto-decrypt keys - user must enter password to switch
            const sessionStore = new E2ESessionStore(identity.userId);
            await sessionStore.init();
            setE2ESessionStore(sessionStore);
          }
        }

        // Load list of identities
        const list = await identityStore.listIdentities();
        setIdentities(list);
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to initialize identity system'));
      } finally {
        setIsLoading(false);
      }
    };

    init();
  }, []);

  const createIdentity = useCallback(
    async (phrase: PoeticIdentity, name: string, password: string) => {
      try {
        setError(null);

        if (!name.trim()) {
          throw new Error('Identity name cannot be empty');
        }

        if (!password.trim()) {
          throw new Error('Password cannot be empty');
        }

        // Derive userId from phrase (private, never shared)
        const userId = derivePoeticUserIdBrowser(phrase);

        // Check if already exists
        const existing = await identityStore.getIdentity(userId);
        if (existing) {
          throw new Error('This identity already exists');
        }

        // Generate E2E keys and encrypt with password
        const e2eIdentity = await e2eKeyStore.generateAndStore(userId, password);

        // Get home region (same derivation as before)
        const homeRegion = `home-${userId.slice(0, 16)}`;

        // Hash password for verification (simple check on login)
        const passwordHash = await crypto.subtle.digest(
          'SHA-256',
          new TextEncoder().encode(password)
        );
        const passwordHashHex = Array.from(new Uint8Array(passwordHash))
          .map((b) => b.toString(16).padStart(2, '0'))
          .join('');

        // Store identity with shareable NAME
        // PASSWORD is NOT stored - it's for backend auth only
        const identity: StoredIdentity = {
          userId,
          phrase,
          name, // Shareable identity name
          createdAt: Date.now(),
          lastUsedAt: Date.now(),
          homeRegion,
          groups: [],
          passwordHash: passwordHashHex,
          e2eKeysSalt: new Uint8Array(), // Deprecated - kept for schema compatibility
          encryptedE2EKeys: '', // Deprecated - keys are in E2EKeyStore now
        };

        await identityStore.saveIdentity(identity);

        // Auto-login the new identity
        await identityStore.setActiveUserId(userId);
        const sessionStore = new E2ESessionStore(userId);
        await sessionStore.init();

        // Update state - auto-login
        setCurrentIdentity(identity);
        setCurrentPassword(password);
        setCurrentE2EIdentity(e2eIdentity);
        setE2ESessionStore(sessionStore);

        // Update local state
        const newIdentities = await identityStore.listIdentities();
        setIdentities(newIdentities);
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to create identity');
        setError(error);
        throw error;
      }
    },
    [identityStore, e2eKeyStore]
  );

  const switchIdentity = useCallback(
    async (userId: UserId, password: string) => {
      try {
        setError(null);

        const identity = await identityStore.getIdentity(userId);
        if (!identity) {
          throw new Error('Identity not found');
        }

        // Verify password hash
        const passwordHash = await crypto.subtle.digest(
          'SHA-256',
          new TextEncoder().encode(password)
        );
        const passwordHashHex = Array.from(new Uint8Array(passwordHash))
          .map((b) => b.toString(16).padStart(2, '0'))
          .join('');

        if (passwordHashHex !== identity.passwordHash) {
          throw new Error('Invalid password');
        }

        // Decrypt or generate E2E keys
        let e2eIdentity: E2EIdentity | null = null;
        try {
          e2eIdentity = await e2eKeyStore.retrieve(userId, password);
          if (!e2eIdentity) {
            // Keys don't exist yet — generate them now
            e2eIdentity = await e2eKeyStore.generateAndStore(userId, password);
          }
        } catch {
          // Decryption failed (wrong password or corrupted) — generate fresh keys
          try {
            e2eIdentity = await e2eKeyStore.generateAndStore(userId, password);
          } catch (genErr) {
            console.warn('Failed to generate E2E keys:', genErr);
          }
        }

        // Update last used
        await identityStore.updateLastUsed(userId);

        // Set as active
        await identityStore.setActiveUserId(userId);

        // Initialize E2E session store for this identity
        const sessionStore = new E2ESessionStore(userId);
        await sessionStore.init();

        // Update state
        // PASSWORD is stored in memory (session only) for backend communication
        // It is NOT persisted to disk
        setCurrentIdentity(identity);
        setCurrentPassword(password); // Keep PASSWORD in memory for backend auth
        setCurrentE2EIdentity(e2eIdentity); // null if generation failed; non-null otherwise
        setE2ESessionStore(sessionStore);

        // Reload identities list to reflect new lastUsedAt
        const newIdentities = await identityStore.listIdentities();
        setIdentities(newIdentities);
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to switch identity');
        setError(error);
        throw error;
      }
    },
    [identityStore, e2eKeyStore]
  );

  const deleteIdentity = useCallback(
    async (userId: UserId) => {
      try {
        setError(null);

        // If deleting current identity, logout first
        if (currentIdentity?.userId === userId) {
          await logout();
        }

        // Delete from stores
        await identityStore.deleteIdentity(userId);
        await e2eKeyStore.delete(userId);

        // Clean up session store database
        try {
          const sessionStoreToDelete = new E2ESessionStore(userId);
          await sessionStoreToDelete.clear();
          sessionStoreToDelete.close();
          // Note: IndexedDB doesn't have direct deleteDatabase in all browsers,
          // but clearing is sufficient
        } catch {
          // Ignore errors during cleanup
        }

        // Update list
        const newIdentities = await identityStore.listIdentities();
        setIdentities(newIdentities);
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to delete identity');
        setError(error);
        throw error;
      }
    },
    [currentIdentity, identityStore, e2eKeyStore]
  );

  const updateIdentity = useCallback(
    async (userId: UserId, updates: Partial<StoredIdentity>) => {
      try {
        setError(null);
        await identityStore.updateIdentity(userId, updates);

        // Update current identity if it's active
        if (currentIdentity?.userId === userId) {
          const updated = await identityStore.getIdentity(userId);
          if (updated) {
            setCurrentIdentity(updated);
          }
        }

        // Reload list
        const newIdentities = await identityStore.listIdentities();
        setIdentities(newIdentities);
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to update identity');
        setError(error);
        throw error;
      }
    },
    [currentIdentity, identityStore]
  );

  const listIdentities = useCallback(async () => {
    try {
      const list = await identityStore.listIdentities();
      setIdentities(list);
      return list;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to list identities');
      setError(error);
      throw error;
    }
  }, [identityStore]);

  const autoLogin = useCallback(
    async (password: string) => {
      try {
        setError(null);
        const activeId = await identityStore.getActiveUserId();

        if (!activeId) {
          return false;
        }

        await switchIdentity(activeId, password);
        return true;
      } catch {
        // Not an error - user just doesn't have auto-login set up
        setError(null);
        return false;
      }
    },
    [identityStore, switchIdentity]
  );

  const logout = useCallback(async () => {
    try {
      setError(null);

      // Close E2E session store
      e2eSessionStore?.close();

      // Clear active session
      await identityStore.setActiveUserId(null);

      // Clear all sensitive state (PASSWORD is cleared from memory)
      setCurrentIdentity(null);
      setCurrentPassword(null); // Clear PASSWORD from memory
      setCurrentE2EIdentity(null);
      setE2ESessionStore(null);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to logout');
      setError(error);
      throw error;
    }
  }, [identityStore, e2eSessionStore]);

  const exportIdentity = useCallback(
    async (userId: UserId) => {
      try {
        setError(null);

        const identity = await identityStore.getIdentity(userId);
        if (!identity) {
          throw new Error('Identity not found');
        }

        // Identities are not encrypted at rest, just export directly
        // This allows sharing the identity (name + phrase) for linking/discovery
        const exportData = {
          version: 1,
          userId,
          phrase: identity.phrase,
          name: identity.name, // Shareable name
          createdAt: identity.createdAt,
        };

        return JSON.stringify(exportData);
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to export identity');
        setError(error);
        throw error;
      }
    },
    [identityStore]
  );

  const importIdentity = useCallback(
    async (data: string, name: string, password: string) => {
      try {
        setError(null);

        if (!name.trim()) {
          throw new Error('Identity name cannot be empty');
        }

        if (!password.trim()) {
          throw new Error('Password cannot be empty');
        }

        const exportData = JSON.parse(data);

        if (exportData.version !== 1) {
          throw new Error('Unsupported export version');
        }

        // Check if identity already exists
        const userId = exportData.userId;
        const existing = await identityStore.getIdentity(userId);
        if (existing) {
          throw new Error('This identity has already been imported');
        }

        // Verify phrase integrity
        if (!exportData.phrase || !exportData.phrase.adjective) {
          throw new Error('Invalid identity data in export');
        }

        // Create the imported identity with new name and password
        const identity: StoredIdentity = {
          userId,
          phrase: exportData.phrase,
          name, // Use new name provided by importer
          createdAt: exportData.createdAt || Date.now(),
          lastUsedAt: Date.now(),
          homeRegion: `home-${userId.slice(0, 16)}`,
          groups: [],
          e2eKeysSalt: new Uint8Array(),
          encryptedE2EKeys: '',
        };

        // Save identity
        await identityStore.saveIdentity(identity);

        // Generate fresh E2E keys encrypted with new password
        await e2eKeyStore.generateAndStore(userId, password);

        // Update list
        const newIdentities = await identityStore.listIdentities();
        setIdentities(newIdentities);
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to import identity');
        setError(error);
        throw error;
      }
    },
    [identityStore, e2eKeyStore]
  );

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    currentIdentity,
    currentPassword, // PASSWORD for backend auth (not persisted)
    identities,
    isAuthenticated: currentIdentity !== null,
    isLoading,
    currentE2EIdentity,
    createIdentity,
    switchIdentity,
    deleteIdentity,
    updateIdentity,
    listIdentities,
    autoLogin,
    logout,
    exportIdentity,
    importIdentity,
    error,
    clearError,
  };
}
